<template>
  <div
    ref="rootRef"
    class="ScrollableRow"
    :class="{ 'ScrollableRow--mobileOnly': mobileOnly }"
    v-bind="keyboardNavAttrs"
    @keydown="handleKeydown"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useDebounceFn } from '@vueuse/core';

const props = withDefaults(defineProps<{
  /** When true, only scroll on mobile; wrap normally on desktop. Default: false (always scroll). */
  mobileOnly?: boolean;
  /** When provided, enables keyboard navigation (ArrowLeft/Right, Home/End) and sets the accessible label on the scroll region. */
  ariaLabel?: string;
}>(), {
  mobileOnly: false,
});

defineOptions({ name: 'ScrollableRow' });

const rootRef = ref<HTMLDivElement>();
const hasOverflow = ref(false);

const RESIZE_DEBOUNCE_MS = 150;
const KEYBOARD_SCROLL_RATIO = 0.8;

type HandledKey = 'ArrowLeft' | 'ArrowRight' | 'Home' | 'End';
const HANDLED_KEYS = new Set<HandledKey>(['ArrowLeft', 'ArrowRight', 'Home', 'End']);

const KEY_ACTIONS: Record<HandledKey, (el: HTMLDivElement, scrollAmount: number) => void> = {
  ArrowLeft: (el, amount) => el.scrollBy({ left: -amount, behavior: 'smooth' }),
  ArrowRight: (el, amount) => el.scrollBy({ left: amount, behavior: 'smooth' }),
  Home: (el) => el.scrollTo({ left: 0, behavior: 'smooth' }),
  End: (el) => el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' }),
};

const keyboardNavAttrs = computed(() => {
  if (!props.ariaLabel) return {};
  return {
    tabindex: hasOverflow.value ? 0 : -1,
    'aria-label': props.ariaLabel,
  };
});

function handleKeydown(event: KeyboardEvent) {
  if (!props.ariaLabel || !rootRef.value || !HANDLED_KEYS.has(event.key as HandledKey)) return;

  event.preventDefault();
  KEY_ACTIONS[event.key as HandledKey](rootRef.value, rootRef.value.clientWidth * KEYBOARD_SCROLL_RATIO);
}

function checkOverflow() {
  if (rootRef.value) {
    hasOverflow.value = rootRef.value.scrollWidth > rootRef.value.clientWidth;
  }
}

const debouncedCheckOverflow = useDebounceFn(checkOverflow, RESIZE_DEBOUNCE_MS);

onMounted(() => {
  if (props.ariaLabel) {
    checkOverflow();
    window.addEventListener('resize', debouncedCheckOverflow);
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', debouncedCheckOverflow);
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .ScrollableRow {
    display: flex;
    align-items: stretch;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }

    @include responsive.mobile {
      margin-inline: calc(-1 * var(--page-padding-x));
      padding-inline: var(--page-padding-x);
    }

    &--mobileOnly {
      flex-wrap: wrap;
      overflow-x: visible;
      margin-inline: 0;
      padding-inline: 0;

      @include responsive.mobile {
        flex-wrap: nowrap;
        overflow-x: auto;
        margin-inline: calc(-1 * var(--page-padding-x));
        padding-inline: var(--page-padding-x);
      }
    }
  }
}
</style>
